import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {Link} from 'shells/link/link';
import {PreviewWithEdit} from 'shells/preview-with-edit/preview-with-edit';
import {RelativeOverlay} from 'shells/relative-overlay/relative-overlay';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {
    FormCreateRulesUpdateInput,
    GetMeeting_meeting_form as Form,
    NextStepsFragment_slackMessage,
    NextStepsFragment_nutshellNotification,
} from 'nutshell-graphql-types';

import {useUpdateWfCreateRules} from '../connected-forms/connected-forms-page/use-update-wf-create-rules';
import {CreateRulesContainer} from '../forms/form-page/create-rules/create-rules-container';
import {NextStepsContainer} from '../forms/form-page/next-steps/next-steps-container';
import {PeanutFormPreview} from '../forms/form-page/peanut-form-preview/peanut-form-preview';

import {nextStepsCacheUpdate} from './helpers';

import styles from './meeting-form.module.css';

type Props = {
    meetingId: string;
    isAdmin: boolean;
    isManagedMeeting: boolean;
    managedByHtmlUrlPath: string | null | undefined;
    form: Form;
    publicFormId: string;
    isManagedTemplate: boolean;
};

export function MeetingForm(props: Props) {
    const updateCreateRules = useUpdateWfCreateRules();
    const onUpdateCreateRules = (input: FormCreateRulesUpdateInput) => {
        return updateCreateRules(input);
    };

    return (
        <>
            {props.isManagedMeeting && (
                <FlashBanner
                    type='warning'
                    // leftAlignContent={true}
                    noRightPadding={true}
                    className='pb-16'
                    hasBorder={true}
                >
                    <div className='flex justify-sb full-width'>
                        Cannot make any changes to managed forms
                        {props.isAdmin && props.managedByHtmlUrlPath && (
                            // @ts-expect-error
                            <Link as={RoutedLink} to={props.managedByHtmlUrlPath} variant='primary'>
                                Go to managed meeting
                            </Link>
                        )}
                    </div>
                </FlashBanner>
            )}
            <PageContentTwoColumn
                withoutOuterPadding={true}
                leftContent={
                    <div className='pos-relative full-height'>
                        {props.isManagedMeeting && (
                            <RelativeOverlay>
                                <div className={styles['overlay-banner']}>
                                    Forms in managed meetings are locked for editing
                                </div>
                            </RelativeOverlay>
                        )}
                        <LabelWithContent
                            label='Preview'
                            spacing={8}
                            isFullHeight={true}
                            content={
                                <PreviewWithEdit
                                    noun='form'
                                    to={
                                        props.isManagedTemplate
                                            ? `/sales/scheduler/managed/${props.meetingId}/editor`
                                            : `/sales/scheduler/${props.meetingId}/editor`
                                    }
                                    preview={
                                        <PeanutFormPreview
                                            publicFormId={props.publicFormId}
                                            instanceId={NutshellFrontendSettings.instanceId}
                                            isNoTouchPreview={true}
                                        />
                                    }
                                />
                            }
                        />
                    </div>
                }
                rightContent={
                    <div className='pos-relative'>
                        {props.isManagedMeeting && <RelativeOverlay />}
                        <LabelWithContent
                            label='Create in Nutshell:'
                            spacing={8}
                            content={
                                <div className={styles['section-container']}>
                                    <CreateRulesContainer
                                        hasAccountFields={
                                            props.form.editableVersion.hasAccountFields
                                        }
                                        hasContactFields={
                                            props.form.editableVersion.hasContactFields
                                        }
                                        hasLeadFields={props.form.editableVersion.hasLeadFields}
                                        formId={props.form.id}
                                        createRules={props.form.createRules}
                                        updateCreateRules={onUpdateCreateRules}
                                        hasLeadSpecialCases={true}
                                    />
                                </div>
                            }
                        />
                        <LabelWithContent
                            label='Notifications'
                            spacing={8}
                            isFullHeight={true}
                            content={
                                <div className={styles['section-container--no-margin']}>
                                    <NextStepsContainer
                                        formId={props.form.id}
                                        description='Notifications will automatically send whenever this meeting is booked.'
                                        bodyText='When this meeting is booked…'
                                        slackMessagePreviewMessage={`${props.form.name} was booked!`}
                                        slackMessagePreviewTopRowText='May 16, 2023 at 10:15 AM'
                                        nextSteps={props.form.nextSteps}
                                        onUpdateSuccess={(input: {
                                            client: any;
                                            nextStepsKey: 'slackMessage' | 'nutshellNotification';
                                            nextStep:
                                                | NextStepsFragment_slackMessage
                                                | NextStepsFragment_nutshellNotification
                                                | null
                                                | undefined;
                                        }) => {
                                            nextStepsCacheUpdate({
                                                client: input.client,
                                                meetingId: props.meetingId,
                                                nextStepsKey: input.nextStepsKey,
                                                nextStep: input.nextStep,
                                            });
                                        }}
                                        onlyAllowNotifyingSelf={true}
                                    />
                                </div>
                            }
                        />
                    </div>
                }
            />
        </>
    );
}
